import { useState, useMemo, useCallback, useRef } from 'react';
import { ordersApi, adminApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { Avatar } from '../components/ui/Avatar';
import { useToast } from '../context/ToastContext';
import {
  formatDate, formatDateTime, formatPrice,
  ORDER_STATUSES, WORKFLOW_STEPS, PAYMENT_LABELS, CATEGORY_COLORS,
  paginate, truncate,
} from '../utils/helpers';

const STATUSES_FILTER = [
  { value: 'all',              label: 'Tous les statuts' },
  { value: 'pending',          label: 'En attente' },
  { value: 'laverie_assigned', label: 'Laverie assignée' },
  { value: 'in_progress',      label: 'En traitement' },
  { value: 'out_for_delivery', label: 'En livraison' },
  { value: 'delivered',        label: 'Livrée' },
  { value: 'completed',        label: 'Terminée' },
  { value: 'cancelled',        label: 'Annulée' },
];

const OrderWorkflow = ({ status }) => {
  const current = ORDER_STATUSES[status]?.step ?? -1;
  if (current === -1) return <Badge className="badge-red">⚠ Commande annulée</Badge>;
  return (
    <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
      <div className="workflow">
        {WORKFLOW_STEPS.map((step, i) => {
          const stepNum = ORDER_STATUSES[step.key]?.step ?? i;
          const done    = current > stepNum;
          const active  = current === stepNum;
          return (
            <div key={step.key} className="workflow-step">
              {i > 0 && <div className="workflow-arrow">›</div>}
              <div className="workflow-node">
                <div className={`workflow-circle ${done ? 'done' : active ? 'active' : ''}`}>
                  {done ? '✓' : step.icon}
                </div>
                <div className="workflow-label">{step.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Fiche d'impression HTML ──────────────────────────────────────────────────
const buildPrintHtml = (d) => `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>Commande ${d.order_number}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #111; padding: 20px; max-width: 800px; margin: auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 14px; border-bottom: 2px solid #111; }
    .logo { font-size: 24px; font-weight: 900; letter-spacing: -1px; }
    .logo span { color: #c8d600; }
    .order-num { font-size: 14px; font-weight: 700; margin-top: 4px; }
    .print-time { font-size: 10px; color: #666; margin-top: 2px; }
    .barcode { font-size: 11px; letter-spacing: 4px; font-weight: 700; margin-top: 6px; }
    .section { margin-bottom: 16px; }
    .section-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; color: #888; margin-bottom: 8px; border-bottom: 1px solid #eee; padding-bottom: 4px; }
    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .info-block { background: #f8f8f8; border-radius: 6px; padding: 10px 12px; }
    .info-label { font-size: 10px; color: #888; margin-bottom: 2px; }
    .info-val { font-size: 12px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th { background: #f0f0f0; padding: 6px 10px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; }
    td { padding: 6px 10px; border-bottom: 1px solid #eee; }
    .total-row td { font-weight: 700; background: #fafafa; }
    .qr-row { display: flex; gap: 20px; margin-top: 10px; }
    .qr-block { text-align: center; }
    .qr-block img { width: 90px; height: 90px; }
    .qr-label { font-size: 10px; color: #888; margin-top: 4px; }
    .footer { margin-top: 20px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 10px; color: #aaa; display: flex; justify-content: space-between; }
    .special { background: #fffbe6; border: 1px solid #ffe066; border-radius: 6px; padding: 8px 12px; font-size: 11px; margin-bottom: 12px; }
    @media print { body { padding: 10px; } @page { margin: 10mm; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="logo"><span>LAV</span>EX</div>
      <div class="order-num">Bon de commande N° ${d.order_number}</div>
      <div class="print-time">Imprimé le ${new Date(d.print_time || Date.now()).toLocaleString('fr-MA')}</div>
      <div class="barcode">${d.barcode || '*' + d.order_number + '*'}</div>
    </div>
    ${d.qr_codes?.client ? `<div class="qr-block"><img src="${d.qr_codes.client}" /><div class="qr-label">QR Client</div></div>` : ''}
  </div>

  <div class="grid-2" style="margin-bottom:16px">
    <div class="info-block">
      <div class="info-label">👤 Client</div>
      <div class="info-val">${d.client?.name || '—'}</div>
      <div style="font-size:11px;color:#555;margin-top:2px">${d.client?.phone || ''}</div>
      <div style="font-size:10px;color:#888">Catégorie ${d.client?.category || '—'}</div>
    </div>
    <div class="info-block">
      <div class="info-label">🏪 Laverie</div>
      <div class="info-val">${d.laverie_name || '—'}</div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">📍 Adresses</div>
    <div class="grid-2">
      <div class="info-block">
        <div class="info-label">Collecte</div>
        <div class="info-val">${d.pickup?.address || '—'}</div>
        ${d.pickup?.scheduled_time ? `<div style="font-size:10px;color:#666;margin-top:3px">Prévu : ${new Date(d.pickup.scheduled_time).toLocaleString('fr-MA')}</div>` : ''}
      </div>
      <div class="info-block">
        <div class="info-label">Livraison</div>
        <div class="info-val">${d.delivery?.address || '—'}</div>
        ${d.delivery?.scheduled_time ? `<div style="font-size:10px;color:#666;margin-top:3px">Prévu : ${new Date(d.delivery.scheduled_time).toLocaleString('fr-MA')}</div>` : ''}
      </div>
    </div>
  </div>

  ${d.special_instructions ? `<div class="special">⚠️ Instructions : ${d.special_instructions}</div>` : ''}

  <div class="section">
    <div class="section-title">🧺 Services commandés</div>
    <table>
      <thead>
        <tr><th>Service</th><th>Quantité</th><th>Prix unitaire</th><th>Total</th></tr>
      </thead>
      <tbody>
        ${(d.services || []).map(s => `
          <tr>
            <td>${s.service_name || s.item_name || '—'}</td>
            <td>${s.quantity} ${s.unit || ''}</td>
            <td>${(s.unit_price || 0).toFixed(2)} MAD</td>
            <td>${(s.total || 0).toFixed(2)} MAD</td>
          </tr>
        `).join('')}
        <tr class="total-row">
          <td colspan="3" style="text-align:right">Sous-total</td>
          <td>${(d.pricing?.subtotal || 0).toFixed(2)} MAD</td>
        </tr>
        <tr>
          <td colspan="3" style="text-align:right">Frais de livraison</td>
          <td>${(d.pricing?.delivery_fee || 0).toFixed(2)} MAD</td>
        </tr>
        ${d.pricing?.discount ? `<tr><td colspan="3" style="text-align:right">Remise</td><td>− ${d.pricing.discount.toFixed(2)} MAD</td></tr>` : ''}
        <tr class="total-row">
          <td colspan="3" style="text-align:right">TOTAL</td>
          <td style="font-size:14px">${(d.pricing?.total || 0).toFixed(2)} MAD</td>
        </tr>
      </tbody>
    </table>
  </div>

  ${d.qr_codes?.pickup ? `
  <div class="section">
    <div class="section-title">QR Codes de suivi</div>
    <div class="qr-row">
      <div class="qr-block"><img src="${d.qr_codes.pickup}" /><div class="qr-label">QR Collecte</div></div>
      ${d.qr_codes.client ? `<div class="qr-block"><img src="${d.qr_codes.client}" /><div class="qr-label">QR Client</div></div>` : ''}
    </div>
  </div>` : ''}

  <div class="footer">
    <span>LAVEX — lavex.ma</span>
    <span>Ref: ${d.order_number}</span>
  </div>

  <script>window.onload = () => window.print();</script>
</body>
</html>`;

export const Commandes = () => {
  const toast = useToast();

  const { data: raw, loading, error, refetch, setData } = useApi(
    () => ordersApi.list()
  );

  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [payFilter,    setPayFilter]    = useState('all');
  const [catFilter,    setCatFilter]    = useState('all');
  const [page,         setPage]         = useState(1);
  const [selected,     setSelected]     = useState(null);
  const [cancelling,   setCancelling]   = useState(false);
  const [printing,     setPrinting]     = useState(false);

  const orders = raw?.data || [];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return orders.filter(o => {
      const matchSearch = !q
        || o.order_number?.toLowerCase().includes(q)
        || o.client_name?.toLowerCase().includes(q)
        || o.laverie_name?.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || o.status === statusFilter;
      const matchPay    = payFilter    === 'all' || o.payment_method === payFilter;
      const matchCat    = catFilter    === 'all' || o.client_category === catFilter;
      return matchSearch && matchStatus && matchPay && matchCat;
    });
  }, [orders, search, statusFilter, payFilter, catFilter]);

  const paged = paginate(filtered, page, 12);
  const resetPage = useCallback(() => setPage(1), []);

  const handleCancel = async (orderId) => {
    setCancelling(true);
    try {
      await ordersApi.cancel(orderId, { reason: 'Annulation par administrateur' });
      setData(prev => ({
        ...prev,
        data: prev.data.map(o => o.order_id === orderId ? { ...o, status: 'cancelled' } : o),
      }));
      toast.success('Commande annulée');
      setSelected(null);
    } catch (err) {
      toast.error(err.message || 'Impossible d\'annuler la commande');
    } finally {
      setCancelling(false);
    }
  };

  // ── Impression (v2.2) — GET /admin/laveries/orders/:id/print ────────────────
  const handlePrint = async (order) => {
    setPrinting(true);
    try {
      // On essaie d'abord l'endpoint dédié (v2.2 — données formatées pour impression)
      let printData;
      try {
        const res = await adminApi.getOrderPrintData(order.order_id);
        printData = res.data;
      } catch {
        // Fallback : on construit les données depuis les détails de la commande
        const detRes = await ordersApi.get(order.order_id);
        const d = detRes.data;
        printData = {
          order_number: d.order_number,
          client: { name: d.client_name, phone: d.client_phone, category: d.client_category },
          laverie_name: d.laverie_name,
          pickup:   { address: d.pickup_address,   scheduled_time: d.pickup_scheduled_time },
          delivery: { address: d.delivery_address, scheduled_time: d.delivery_scheduled_time },
          services: d.service_details || [],
          pricing: {
            subtotal: d.subtotal, delivery_fee: d.delivery_fee,
            discount: d.discount_amount, total: d.total_price,
          },
          qr_codes:            { pickup: d.qr_code_pickup, client: d.qr_code_client },
          special_instructions: d.special_instructions,
          barcode: `*${d.order_number}*`,
          print_time: new Date().toISOString(),
        };
      }

      // Ouvrir dans une nouvelle fenêtre et lancer l'impression
      const win = window.open('', '_blank', 'width=900,height=700');
      win.document.write(buildPrintHtml(printData));
      win.document.close();
    } catch (err) {
      toast.error(err.message || 'Impossible de préparer l\'impression');
    } finally {
      setPrinting(false);
    }
  };

  const canCancel = (status) => ['pending', 'laverie_assigned', 'laverie_confirmed'].includes(status);

  if (loading) return <Loading text="Chargement des commandes..." />;
  if (error)   return <ApiError message={error} onRetry={refetch} />;

  return (
    <div>
      <div className="filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input className="search-input" placeholder="N° commande, client, laverie..."
            value={search} onChange={e => { setSearch(e.target.value); resetPage(); }} />
        </div>
        <select className="filter-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); resetPage(); }}>
          {STATUSES_FILTER.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select className="filter-select" value={payFilter} onChange={e => { setPayFilter(e.target.value); resetPage(); }}>
          <option value="all">Paiement: Tous</option>
          <option value="cash">Espèces</option>
          <option value="online">En ligne</option>
          <option value="points">Points</option>
        </select>
        <select className="filter-select" value={catFilter} onChange={e => { setCatFilter(e.target.value); resetPage(); }}>
          <option value="all">Catégorie: Tous</option>
          <option value="A">Catégorie A</option>
          <option value="B">Catégorie B</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{filtered.length} résultats</span>
        <button className="btn btn-ghost btn-sm" onClick={refetch} title="Rafraîchir">↺</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>N° Commande</th><th>Client</th><th>Laverie</th><th>Statut</th>
                <th>Montant</th><th>Paiement</th><th>Date</th><th></th>
              </tr>
            </thead>
            <tbody>
              {paged.items.map(o => {
                const sc = ORDER_STATUSES[o.status]       || { label: o.status, cls: 'badge-gray' };
                const pc = PAYMENT_LABELS[o.payment_method] || { label: o.payment_method, cls: 'badge-gray' };
                return (
                  <tr key={o.order_id}>
                    <td><span className="text-mono">{o.order_number?.slice(-10)}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Avatar name={o.client_name} size="sm" />
                        <div>
                          <div className="td-main">{o.client_name}</div>
                          <div className="td-sub">
                            <Badge className={`badge ${CATEGORY_COLORS[o.client_category]}`} dot={false}>
                              Cat. {o.client_category}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="td-main">{truncate(o.laverie_name, 25)}</div>
                      <div className="td-sub">Cat. {o.laverie_category}</div>
                    </td>
                    <td><Badge className={sc.cls}>{sc.label}</Badge></td>
                    <td style={{ fontWeight: 600, color: 'var(--text)' }}>{formatPrice(o.total_price)}</td>
                    <td><Badge className={pc.cls} dot={false}>{pc.label}</Badge></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(o.created_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => setSelected(o)}>Voir</button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handlePrint(o)}
                          disabled={printing}
                          title="Imprimer la commande"
                        >
                          🖨️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {paged.items.length === 0 && (
            <EmptyState icon="📋" title="Aucune commande" text="Modifiez les filtres ou rafraîchissez." />
          )}
        </div>
        {paged.total > 0 && (
          <div style={{ padding: '0 16px 16px' }}>
            <Pagination {...paged} onPage={setPage} />
          </div>
        )}
      </div>

      {/* ── DETAIL MODAL ─────────────────────────────────────────────────────── */}
      <Modal
        open={!!selected} onClose={() => setSelected(null)}
        title={`Commande #${selected?.order_number?.slice(-10)}`}
        size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setSelected(null)}>Fermer</button>
          {selected && (
            <button
              className="btn btn-ghost"
              onClick={() => handlePrint(selected)}
              disabled={printing}
            >
              {printing ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '🖨️ Imprimer'}
            </button>
          )}
          {selected && canCancel(selected.status) && (
            <button className="btn btn-danger" onClick={() => handleCancel(selected.order_id)} disabled={cancelling}>
              {cancelling
                ? <span className="spinner" style={{ width: 14, height: 14 }} />
                : '✕ Annuler la commande'}
            </button>
          )}
        </>}
      >
        {selected && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Progression</div>
              <OrderWorkflow status={selected.status} />
            </div>
            <div className="divider" />
            <div className="grid-2" style={{ gap: 20 }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Détails financiers</div>
                {[
                  { l: 'Sous-total',      v: formatPrice(selected.subtotal) },
                  { l: 'Frais livraison', v: formatPrice(selected.delivery_fee) },
                  { l: 'Commission',      v: formatPrice(selected.commission) },
                  { l: 'Total',           v: <strong>{formatPrice(selected.total_price)}</strong> },
                  { l: 'Paiement',        v: <Badge className={PAYMENT_LABELS[selected.payment_method]?.cls}>{PAYMENT_LABELS[selected.payment_method]?.label}</Badge> },
                  { l: 'Points gagnés',   v: `+${selected.points_earned  || 0} pts` },
                  { l: 'Points utilisés', v: `−${selected.points_used    || 0} pts` },
                ].map((r, i) => (
                  <div key={i} className="info-row">
                    <span className="info-label">{r.l}</span>
                    <span className="info-value">{r.v}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Acteurs</div>
                {[
                  { l: 'Client',            v: `${selected.client_name} (Cat. ${selected.client_category})` },
                  { l: 'Tél. client',       v: selected.client_phone },
                  { l: 'Laverie',           v: selected.laverie_name },
                  { l: 'Tél. laverie',      v: selected.laverie_phone },
                  { l: 'Livreur collecte',  v: selected.livreur_pickup_name   || '—' },
                  { l: 'Livreur livraison', v: selected.livreur_delivery_name || '—' },
                  { l: 'Distance',          v: selected.distance_km ? `${selected.distance_km} km` : '—' },
                ].map((r, i) => (
                  <div key={i} className="info-row">
                    <span className="info-label">{r.l}</span>
                    <span className="info-value">{r.v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="divider" />
            <div className="grid-2" style={{ gap: 12 }}>
              {[
                { label: '📍 Adresse de collecte',  val: selected.pickup_address },
                { label: '🏠 Adresse de livraison', val: selected.delivery_address },
              ].map((a, i) => (
                <div key={i} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{a.label}</div>
                  <div style={{ fontSize: 13 }}>{a.val}</div>
                </div>
              ))}
            </div>
            {selected.special_instructions && (
              <div className="inline-alert inline-alert-yellow" style={{ marginTop: 12 }}>
                <span>⚠️</span>
                <div><strong>Instructions spéciales :</strong> {selected.special_instructions}</div>
              </div>
            )}
            <div className="divider" />

            {/* ── QR CODES v2.4 ── */}
            <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8 }}>QR Codes</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {[
                {
                  label:     'QR1 — Pickup client',
                  code:      selected.qr_code_client_pickup,
                  scanned:   !!selected.pickup_actual_time,
                  scanTime:  selected.pickup_actual_time,
                  available: true,
                },
                {
                  label:     'QR3 — Pickup laverie',
                  code:      selected.qr_code_laverie_to_delivery,
                  scanned:   !!selected.picked_from_laverie_time,
                  scanTime:  selected.picked_from_laverie_time,
                  available: ['delivery_assigned', 'out_for_delivery', 'delivered'].includes(selected.status),
                },
                {
                  label:     'QR4 — Delivery client',
                  code:      selected.qr_code_client_delivery,
                  scanned:   !!selected.delivery_actual_time,
                  scanTime:  selected.delivery_actual_time,
                  available: ['out_for_delivery', 'delivered'].includes(selected.status),
                },
              ].map((qr, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: 'var(--bg3)', borderRadius: 10, padding: '10px 14px',
                  opacity: qr.available ? 1 : 0.45,
                  borderLeft: `3px solid ${!qr.available ? 'var(--border)' : qr.scanned ? 'var(--green)' : 'var(--orange)'}`,
                }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{qr.label}</div>
                    {qr.available && qr.code && (
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>{qr.code}</div>
                    )}
                    {qr.scanned && qr.scanTime && (
                      <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 3 }}>
                        ✓ Scanné le {formatDateTime(qr.scanTime)}
                      </div>
                    )}
                  </div>
                  <div>
                    {!qr.available
                      ? <Badge className="badge-gray">Non généré</Badge>
                      : qr.scanned
                        ? <Badge className="badge-green">✓ Scanné</Badge>
                        : <Badge className="badge-orange">⏳ En attente</Badge>
                    }
                  </div>
                </div>
              ))}
            </div>

            {/* ── TIMELINE LAVERIE v2.4 ── */}
            <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Traitement laverie</div>
            <div style={{ position: 'relative', paddingLeft: 20, marginBottom: 16 }}>
              {/* Ligne verticale */}
              <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, background: 'var(--border)' }} />
              {[
                { label: 'Arrivée à la laverie',  ts: selected.received_at_laverie_time,                 done: !!selected.received_at_laverie_time },
                { label: 'Fiche imprimée',         ts: selected.receipt_printed_at,                       done: !!selected.receipt_printed           },
                { label: 'Prêt pour livraison',    ts: selected.status === 'ready' ? selected.updated_at : null, done: selected.status === 'ready'  },
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10, position: 'relative' }}>
                  <div style={{
                    width: 14, height: 14, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                    background: step.done ? 'var(--green)' : 'var(--border)',
                    border: `2px solid ${step.done ? 'var(--green)' : 'var(--border2)'}`,
                    zIndex: 1,
                  }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: step.done ? 600 : 400, color: step.done ? 'var(--text)' : 'var(--text3)' }}>
                      {step.label}
                    </div>
                    {step.ts && (
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                        {formatDateTime(step.ts)}
                      </div>
                    )}
                  </div>
                  {step.done && (
                    <span style={{ marginLeft: 'auto', color: 'var(--green)', fontSize: 14 }}>✓</span>
                  )}
                </div>
              ))}
            </div>

            {/* ── COURSES LIVREURS v2.4 ── */}
            {selected.courses?.length > 0 && (
              <>
                <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Courses livreurs</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selected.courses.map((c, i) => (
                    <div key={i} style={{
                      background: 'var(--bg3)', borderRadius: 10, padding: '10px 14px',
                      borderLeft: `3px solid ${c.course_type === 'pickup' ? 'var(--orange)' : 'var(--blue)'}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <Badge className={c.course_type === 'pickup' ? 'badge-orange' : 'badge-blue'}>
                          {c.course_type === 'pickup' ? '📦 Pickup' : '🚚 Delivery'}
                        </Badge>
                        <Badge className={c.status === 'completed' ? 'badge-green' : 'badge-yellow'}>
                          {c.status === 'completed' ? '✓ Terminée' : '⏳ En cours'}
                        </Badge>
                      </div>
                      <div className="info-row" style={{ padding: '2px 0' }}>
                        <span className="info-label">👤 Livreur</span>
                        <span className="info-value">{c.livreur_name}</span>
                      </div>
                      {c.start_time && (
                        <div className="info-row" style={{ padding: '2px 0' }}>
                          <span className="info-label">🕐 Début</span>
                          <span className="info-value">{formatDateTime(c.start_time)}</span>
                        </div>
                      )}
                      {c.status === 'completed' && (
                        <>
                          {c.duration_minutes && (
                            <div className="info-row" style={{ padding: '2px 0' }}>
                              <span className="info-label">⏱️ Durée</span>
                              <span className="info-value">{c.duration_minutes} min</span>
                            </div>
                          )}
                          {c.distance_km && (
                            <div className="info-row" style={{ padding: '2px 0' }}>
                              <span className="info-label">📍 Distance</span>
                              <span className="info-value">{Number(c.distance_km).toFixed(1)} km</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="divider" />
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>Créée : {formatDateTime(selected.created_at)}</div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>MàJ : {formatDateTime(selected.updated_at)}</div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
